import {
  FORM_TEMPLATE_ARCHETYPES,
  FORM_TEMPLATE_PHASES,
  FORM_TEMPLATE_TRADES,
  type FormTemplateArchetype,
  type FormTemplatePhase,
  type FormTemplateTrade,
} from '~convex/lib/formTemplateEnums'
import { TEMPLATE_CATEGORIES, type TemplateCategory } from '~convex/lib/templateEnums'

/**
 * The Templates world's URL state.
 *
 * `type` is the open branch of the type tree — the same arrangement the
 * products tree uses, where the open category IS the search param rather than
 * a stored open/closed flag. `model` is the opened template, so one someone
 * found is a link they can send.
 */
export type TemplatesSearch = {
  archetype?: FormTemplateArchetype
  /** The opened model's handle: `form:<id>`, `doc:<id>` or `soon:<key>`. */
  model?: string
  phase?: FormTemplatePhase
  trade?: FormTemplateTrade
  type?: TemplateCategory
}

const isCategory = (value: unknown): value is TemplateCategory =>
  typeof value === 'string' && TEMPLATE_CATEGORIES.some((category) => category === value)

/** Primary filter used by template links created before the taxonomy expansion. */
const enumValue = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  typeof value === 'string' && values.some((candidate) => candidate === value)
    ? (value as T)
    : undefined

const legacyLensCategory = (
  value: unknown,
  phase: FormTemplatePhase | undefined
): TemplateCategory | undefined => {
  if (value === 'briefing') {
    return 'briefings'
  }
  if (value === 'checklist') {
    // Before the type tree, all checklist templates lived under one lens.
    // The expanded taxonomy puts phased checklists under Site work, so an old
    // bookmarked phase filter must open that branch rather than an empty
    // generic-checklist shelf.
    return phase === undefined ? 'checklists' : 'sitework'
  }
}

export const decodeTemplatesSearch = (search: Record<string, unknown>): TemplatesSearch => {
  const phase = enumValue(FORM_TEMPLATE_PHASES, search.phase)
  return {
    archetype: enumValue(FORM_TEMPLATE_ARCHETYPES, search.archetype),
    model: typeof search.model === 'string' ? search.model : undefined,
    phase,
    trade: enumValue(FORM_TEMPLATE_TRADES, search.trade),
    type: isCategory(search.type) ? search.type : legacyLensCategory(search.lens, phase),
  }
}
